import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { useCurrency } from '@/hooks/useCurrency';
import { formatPriceWithCurrency } from '@/utils/currency';
import { FileText, Download, RefreshCw, Calendar, Eye, Printer, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

interface VendorInvoice {
  id: string;
  invoice_number: string;
  year: number;
  month: number;
  total_sales_cents: number;
  commission_cents: number;
  net_amount_cents: number;
  currency: string;
  orders_count: number;
  products_sold: number;
  generated_at: string;
}

const MONTH_NAMES = ['Janvier','Février','Mars','Avril','Mai','Juin','Juillet','Août','Septembre','Octobre','Novembre','Décembre'];

export const VendorInvoiceGenerator = () => {
  const { user } = useAuth();
  const { displayCurrency, convertPrice } = useCurrency();
  const [generating, setGenerating] = useState(false);
  const [invoices, setInvoices] = useState<VendorInvoice[]>([]);
  const [loadingInvoices, setLoadingInvoices] = useState(true);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear().toString());
  const [selectedMonth, setSelectedMonth] = useState((new Date().getMonth() + 1).toString());

  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 5 }, (_, i) => currentYear - i);
  const months = MONTH_NAMES.map((label, i) => ({ value: String(i + 1), label }));

  const fetchInvoices = async () => {
    if (!user) return;
    setLoadingInvoices(true);
    const { data } = await supabase
      .from('vendor_invoices')
      .select('*')
      .eq('vendor_id', user.id)
      .order('year', { ascending: false })
      .order('month', { ascending: false });
    setInvoices(data || []);
    setLoadingInvoices(false);
  };

  useEffect(() => { fetchInvoices(); }, [user]);

  const handleGenerateInvoice = async () => {
    try {
      setGenerating(true);
      const { data, error } = await supabase.rpc('generate_vendor_invoice', {
        p_vendor_id: user?.id,
        p_year: parseInt(selectedYear),
        p_month: parseInt(selectedMonth)
      });
      if (error) throw error;
      if (data && data.success) {
        toast.success('Facture générée avec succès', { description: `Numéro: ${data.invoice_number}` });
        fetchInvoices();
      } else {
        toast.error(data?.error || 'Erreur lors de la génération');
      }
    } catch (error: any) {
      toast.error(error.message || 'Erreur lors de la génération de la facture');
    } finally {
      setGenerating(false);
    }
  };

  const handleDeleteInvoice = async (inv: VendorInvoice) => {
    if (!confirm(`Supprimer la facture ${inv.invoice_number} ? Vous pourrez la régénérer.`)) return;
    const { error } = await supabase.from('vendor_invoices').delete().eq('id', inv.id);
    if (error) {
      toast.error('Erreur lors de la suppression');
    } else {
      toast.success('Facture supprimée — vous pouvez la régénérer');
      fetchInvoices();
    }
  };

  const handlePrintInvoice = async (inv: VendorInvoice) => {
    const fmt = (cents: number) => formatPriceWithCurrency(cents, inv.currency, displayCurrency, convertPrice);

    // Fetch orders for this period
    const startDate = new Date(Date.UTC(inv.year, inv.month - 1, 1)).toISOString();
    const endDate = new Date(Date.UTC(inv.year, inv.month, 1)).toISOString();
    const { data: orders } = await supabase
      .from('orders')
      .select('order_number, amount_cents, currency, created_at, products(title)')
      .eq('vendor_id', user?.id)
      .in('status', ['PAID', 'AVAILABLE_FOR_PICKUP', 'PICKED_UP'])
      .gte('created_at', startDate)
      .lt('created_at', endDate)
      .order('created_at', { ascending: true });

    const orderRows = (orders || []).map(o => {
      const title = (o.products as any)?.title || 'Produit';
      const date = new Date(o.created_at).toLocaleDateString('fr-FR');
      return `<tr><td>${o.order_number}</td><td>${title}</td><td>${date}</td><td style="text-align:right">${fmt(o.amount_cents)}</td></tr>`;
    }).join('');

    const html = `<!DOCTYPE html><html lang="fr"><head><meta charset="UTF-8">
<title>Facture ${inv.invoice_number}</title>
<style>
  body { font-family: Arial, sans-serif; max-width: 750px; margin: 40px auto; color: #222; }
  .header { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 32px; }
  .logo { font-size: 24px; font-weight: bold; color: #0ea5e9; }
  .invoice-title { text-align: right; }
  .invoice-title h1 { font-size: 28px; color: #0ea5e9; margin: 0; }
  .invoice-title p { margin: 2px 0; color: #666; font-size: 13px; }
  h2 { font-size: 14px; color: #555; margin: 28px 0 8px; text-transform: uppercase; letter-spacing: 0.05em; }
  table { width: 100%; border-collapse: collapse; }
  th { background: #f0f9ff; padding: 8px 12px; text-align: left; font-size: 12px; border-bottom: 2px solid #0ea5e9; }
  td { padding: 8px 12px; border-bottom: 1px solid #e5e7eb; font-size: 13px; }
  .subtotal-row td { font-weight: bold; background: #f9fafb; }
  .commission-row td { color: #dc2626; }
  .net-row td { font-weight: bold; font-size: 15px; color: #16a34a; background: #f0fdf4; }
  .footer { margin-top: 40px; font-size: 12px; color: #9ca3af; text-align: center; }
  @media print { button { display: none; } }
</style></head><body>
<div class="header">
  <div class="logo">Nazandoo</div>
  <div class="invoice-title">
    <h1>FACTURE</h1>
    <p><strong>${inv.invoice_number}</strong></p>
    <p>Période : ${MONTH_NAMES[inv.month - 1]} ${inv.year}</p>
    <p>Générée le : ${new Date(inv.generated_at).toLocaleDateString('fr-FR')}</p>
  </div>
</div>

<h2>Détail des ventes</h2>
<table>
  <thead><tr><th>N° Commande</th><th>Article</th><th>Date</th><th style="text-align:right">Montant</th></tr></thead>
  <tbody>
    ${orderRows || '<tr><td colspan="4" style="text-align:center;color:#999">Aucune commande</td></tr>'}
  </tbody>
</table>

<h2>Récapitulatif</h2>
<table>
  <tbody>
    <tr class="subtotal-row"><td colspan="3">Total ventes brutes (${inv.orders_count} commande${inv.orders_count !== 1 ? 's' : ''})</td><td style="text-align:right">${fmt(inv.total_sales_cents)}</td></tr>
    <tr class="commission-row"><td colspan="3">Commission Nazandoo</td><td style="text-align:right">- ${fmt(inv.commission_cents)}</td></tr>
    <tr class="net-row"><td colspan="3">Net à percevoir</td><td style="text-align:right">${fmt(inv.net_amount_cents)}</td></tr>
  </tbody>
</table>
<div class="footer"><p>Nazandoo Marketplace — Kinshasa, RD Congo — contact@nazandoo.com</p></div>
<script>window.onload = () => window.print();</script>
</body></html>`;
    const win = window.open('', '_blank');
    if (win) { win.document.write(html); win.document.close(); }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <FileText className="h-5 w-5" />
          Générer une facture
        </CardTitle>
        <CardDescription>Créez une facture mensuelle pour vos revenus</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <label className="text-sm font-medium">Année</label>
            <Select value={selectedYear} onValueChange={setSelectedYear}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {years.map(year => <SelectItem key={year} value={year.toString()}>{year}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <label className="text-sm font-medium">Mois</label>
            <Select value={selectedMonth} onValueChange={setSelectedMonth}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {months.map(m => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
        <Button onClick={handleGenerateInvoice} disabled={generating} className="w-full">
          {generating ? <><RefreshCw className="h-4 w-4 mr-2 animate-spin" />Génération en cours...</> : <><Download className="h-4 w-4 mr-2" />Générer la facture</>}
        </Button>
        <div className="text-xs text-muted-foreground bg-muted p-3 rounded-md">
          <Calendar className="h-3 w-3 inline mr-1" />
          La facture inclura toutes les transactions du mois sélectionné
        </div>
      </CardContent>

      {(loadingInvoices || invoices.length > 0) && (
        <>
          <Separator />
          <CardHeader className="pb-2 pt-4">
            <CardTitle className="text-base flex items-center gap-2">
              <Eye className="h-4 w-4" />
              Mes factures
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loadingInvoices ? (
              <p className="text-sm text-muted-foreground text-center py-4">Chargement...</p>
            ) : (
              <div className="space-y-3">
                {invoices.map(inv => (
                  <div key={inv.id} className="border rounded-lg p-4 space-y-3">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-mono text-sm font-semibold">{inv.invoice_number}</p>
                        <p className="text-xs text-muted-foreground">
                          {MONTH_NAMES[inv.month - 1]} {inv.year} — générée le {new Date(inv.generated_at).toLocaleDateString('fr-FR')}
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge variant="outline">{inv.orders_count} commande{inv.orders_count !== 1 ? 's' : ''}</Badge>
                        <Button size="sm" variant="outline" onClick={() => handlePrintInvoice(inv)} title="Imprimer / Télécharger">
                          <Printer className="h-4 w-4" />
                        </Button>
                        <Button size="sm" variant="ghost" className="text-red-500 hover:text-red-700 hover:bg-red-50" onClick={() => handleDeleteInvoice(inv)} title="Supprimer (pour régénérer)">
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                    <div className="grid grid-cols-3 gap-2 text-sm">
                      <div className="bg-muted rounded p-2 text-center">
                        <p className="text-xs text-muted-foreground">Ventes brutes</p>
                        <p className="font-semibold">{formatPriceWithCurrency(inv.total_sales_cents, inv.currency, displayCurrency, convertPrice)}</p>
                      </div>
                      <div className="bg-muted rounded p-2 text-center">
                        <p className="text-xs text-muted-foreground">Commission</p>
                        <p className="font-semibold text-red-600">-{formatPriceWithCurrency(inv.commission_cents, inv.currency, displayCurrency, convertPrice)}</p>
                      </div>
                      <div className="bg-green-50 border border-green-200 rounded p-2 text-center">
                        <p className="text-xs text-muted-foreground">Net perçu</p>
                        <p className="font-bold text-green-700">{formatPriceWithCurrency(inv.net_amount_cents, inv.currency, displayCurrency, convertPrice)}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </>
      )}
    </Card>
  );
};
